import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'

type TransactionSelectionContextValue = {
  selectedTransactionIds: string[]
  selectedCount: number
  toggleTransaction: (transactionId: string) => void
  selectAll: (transactionIds: string[]) => void
  deselectAll: () => void
  isTransactionSelected: (transactionId: string) => boolean
}

const TransactionSelectionContext =
  createContext<TransactionSelectionContextValue | null>(null)

export const TransactionSelectionProvider = ({
  children,
}: PropsWithChildren) => {
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<
    string[]
  >([])

  const toggleTransaction = useCallback((transactionId: string) => {
    setSelectedTransactionIds(current =>
      current.includes(transactionId)
        ? current.filter(id => id !== transactionId)
        : [...current, transactionId]
    )
  }, [])

  const selectAll = useCallback((transactionIds: string[]) => {
    setSelectedTransactionIds(transactionIds)
  }, [])

  const deselectAll = useCallback(() => {
    setSelectedTransactionIds([])
  }, [])

  const isTransactionSelected = useCallback(
    (transactionId: string) => selectedTransactionIds.includes(transactionId),
    [selectedTransactionIds]
  )

  const value = useMemo<TransactionSelectionContextValue>(
    () => ({
      selectedTransactionIds,
      selectedCount: selectedTransactionIds.length,
      toggleTransaction,
      selectAll,
      deselectAll,
      isTransactionSelected,
    }),
    [
      selectedTransactionIds,
      toggleTransaction,
      selectAll,
      deselectAll,
      isTransactionSelected,
    ]
  )

  return (
    <TransactionSelectionContext.Provider value={value}>
      {children}
    </TransactionSelectionContext.Provider>
  )
}

export const useTransactionSelection = () => {
  const context = useContext(TransactionSelectionContext)
  if (!context) {
    throw new Error(
      'useTransactionSelection must be used within a TransactionSelectionProvider'
    )
  }
  return context
}

export const useOptionalTransactionSelection = () =>
  useContext(TransactionSelectionContext)
